import { useEffect, useMemo, useRef, useState } from 'react';
import { useContext } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { auth } from 'firebase/firebase';
import moment from 'moment';

import { getRange } from '@src/packages/dashboard/utils';
import { useElasticDivisionIds } from '@src/packages/utils';
import { apiCallable } from '@src/util/cloud-functions/apiClient';

import { SlidePanelContext } from 'packages/dashboard/components/SlidePanel/SlidePanelContext';

/**
 * Hook to handle data fetching and transformation for dashboard widgets
 * Supports two strategies: cloudFunction (for charts) and custom hook (for tables)
 */
export const useWidgetData = ({ widget, filters, config, refresh, widgetTableFilterHandlers }) => {
  const {
    op = null,
    useDataHook = null,
    buildParams = null,
    transformData = null,
    additionalProps = {},
  } = config;

  const boardFilters = filters;
  const widgetFilterValues = filters?.widgetFilterValues ?? null;
  const { queryParams } = widget;
  const { boardSelected } = useContext(SlidePanelContext);
  const chartGroupBy = filters.groupBy;
  const intl = useIntl();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [additionalState, setAdditionalState] = useState({});
  const onDataChangeRef = useRef(config.onDataChange);
  const previousHookDataRef = useRef(null);

  useEffect(() => {
    onDataChangeRef.current = config.onDataChange;
  }, [config.onDataChange]);

  /* Role-aware query scope: `[]` (the API's "whole organization" form) only for org-wide
   * roles, explicit ids for a division-scoped user. Matches the task, schedule and
   * checkpoint dashboards. */
  const queryDivisionIds = useElasticDivisionIds();
  const orgId = useSelector(state => state.organizations.organization.id);

  // Validate that we have the minimum required data to make the request
  // Note: For charts, we need orgId and op; queryDivisionIds may legitimately be empty
  const isValidRequest = useMemo(() => {
    if (useDataHook) return true; // Tables handle validation internally
    return !!orgId && !!op;
  }, [useDataHook, orgId, op]);

  // Build common parameters for cloud functions (only for charts)
  const commonParams = useMemo(() => {
    if (useDataHook) return null; // Tables handle their own params

    const {
      dateRange,
      relative: { dateType, relativeAmount, isRelative },
    } = filters;
    const fromByDefault = moment().subtract(7, 'days');
    let dateRangeFormated = dateRange || [fromByDefault, moment()];
    if (dateRange?.length === 2) {
      dateRangeFormated = [moment(dateRange[0]), moment(dateRange[1])];
    }

    return {
      organizationId: orgId,
      divisionIds: boardSelected?.divId ? [boardSelected.divId] : queryDivisionIds,
      interval: chartGroupBy,
      range: getRange(dateRangeFormated, dateType, isRelative, relativeAmount),
      uid: auth.currentUser.uid,
    };
  }, [
    useDataHook,
    orgId,
    queryDivisionIds,
    boardSelected?.divId,
    chartGroupBy,
    filters,
  ]);

  // Final parameters for cloud function (only for charts)
  const cloudFunctionParams = useMemo(() => {
    if (!commonParams || !buildParams || !isValidRequest) return null;
    return buildParams(queryParams, commonParams, widgetFilterValues);
  }, [commonParams, buildParams, queryParams, isValidRequest, widgetFilterValues]);

  // Call the API operation directly (only for charts)
  useEffect(() => {
    if (useDataHook || !cloudFunctionParams || !op) {
      return;
    }

    const isValidRequest = !!orgId;
    if (!isValidRequest) {
      return;
    }

    const callCloudFunction = apiCallable(op);
    setLoading(true);

    callCloudFunction(cloudFunctionParams)
      .then(result => {
        const wrappedResult = { data: result.data };
        try {
          const transformed = transformData(
            wrappedResult,
            filters,
            intl,
            chartGroupBy,
            queryParams,
          );
          setData(transformed.data || transformed);
          if (transformed.additionalState) {
            setAdditionalState(transformed.additionalState);
          }
        } catch (error) {
          console.error('Error transforming chart data:', error, {
            result: result.data,
            wrappedResult,
            errorMessage: error.message,
            errorStack: error.stack,
          });
          setData({
            metrics: [],
            info: {
              totalEvents: 0,
              division: {},
              eventType: {},
              trigger: {},
            },
          });
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error calling cloud function:', err);
        setLoading(false);
        setData({
          metrics: [],
          info: {
            totalEvents: 0,
            division: {},
            eventType: {},
            trigger: {},
          },
        });
      });
  }, [
    useDataHook,
    cloudFunctionParams,
    op,
    orgId,
    refresh,
    transformData,
    filters,
    intl,
    chartGroupBy,
    queryParams,
  ]);

  // Custom hook for tables
  const useDummyHook = () => ({ data: null, loading: false });
  const actualHook = useDataHook || useDummyHook;
  const hookResult = actualHook({
    queryParams,
    filters: boardFilters,
    widgetFilterValues,
    widgetTableFilterHandlers,
    refresh,
    buildParams,
    orgId,
    queryDivisionIds,
    boardSelected,
    chartGroupBy,
    widget,
    eventTypes: additionalProps.eventTypes,
    elementsRef: additionalProps.elementsRef,
    ...additionalProps,
  });

  // Sync data and loading from custom hook (tables)
  useEffect(() => {
    if (useDataHook && hookResult) {
      const currentData = hookResult.data;
      const currentLoading = hookResult.loading;
      const previousData = previousHookDataRef.current?.data;
      const previousLoading = previousHookDataRef.current?.loading;

      const loadingChanged = currentLoading !== previousLoading;
      const metricsLengthChanged = currentData?.metrics?.length !== previousData?.metrics?.length;
      const totalEventsChanged = currentData?.totalEvents !== previousData?.totalEvents;
      const currentPageChanged = currentData?.currentPage !== previousData?.currentPage;
      const pageSizeChanged = currentData?.pageSize !== previousData?.pageSize;

      const dataChanged =
        metricsLengthChanged || totalEventsChanged || currentPageChanged || pageSizeChanged;

      if (loadingChanged || dataChanged) {
        setData(currentData || null);
        setLoading(currentLoading || false);
        previousHookDataRef.current = { data: currentData, loading: currentLoading };
      }
    }
  }, [useDataHook, hookResult]);

  // Handle onDataChange callback
  useEffect(() => {
    if (onDataChangeRef.current && data) {
      onDataChangeRef.current(data, additionalState, setAdditionalState);
    }
  }, [data, additionalState]);

  return {
    data,
    loading,
    additionalState,
  };
};
